// @ts-nocheck
/** Shared unlock animation: connection line draw + border trace + golden reveal. */

export const PICK_UNLOCK_START_MS = 180;
export const PICK_UNLOCK_EDGE_MS = 520;
export const PICK_UNLOCK_DOT_MS = 100;
export const PICK_UNLOCK_BORDER_MS = 550;
export const PICK_BORDER_R = 12;

/** Snappier unlock timing for skill-draft picks (shop keeps full timing). */
export const DRAFT_PICK_UNLOCK_TIMING = {
  startMs: 40,
  edgeMs: 280,
  dotMs: 30,
  borderMs: 320,
};

/** Border trace from anchor on one edge, branching both ways to meet at bottom center. */
export function borderTracePaths(w, h, r, ax, ay) {
  const s = 0;
  r = Math.min(r, w * 0.5 - 0.5, h * 0.5 - 0.5);
  if (r < 1) r = 1;
  const ex = w * 0.5;
  const ey = h - s;
  const top = s;
  const bot = h - s;
  const left = s;
  const right = w - s;
  const tol = r + 4;
  const onTop = ay <= tol;
  const onLeft = !onTop && ax <= tol;
  const onRight = !onTop && ax >= w - tol;
  const sx = Math.max(r + s, Math.min(right - r - s, ax));
  const sy = Math.max(r + s, Math.min(bot - r - s, ay));

  if (onTop) {
    const start = Math.max(r + s, Math.min(right - r - s, ax));
    const traceRight = [
      `M ${start} ${top}`,
      `L ${right - r} ${top}`,
      `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
      `L ${right} ${bot - r}`,
      `A ${r} ${r} 0 0 1 ${right - r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    const traceLeft = [
      `M ${start} ${top}`,
      `L ${left + r} ${top}`,
      `A ${r} ${r} 0 0 0 ${left} ${top + r}`,
      `L ${left} ${bot - r}`,
      `A ${r} ${r} 0 0 0 ${left + r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    return { traceRight, traceLeft };
  }
  if (onLeft) {
    const traceDown = [
      `M ${left} ${sy}`,
      `L ${left} ${bot - r}`,
      `A ${r} ${r} 0 0 0 ${left + r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    const traceUp = [
      `M ${left} ${sy}`,
      `L ${left} ${top + r}`,
      `A ${r} ${r} 0 0 0 ${left + r} ${top}`,
      `L ${right - r} ${top}`,
      `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
      `L ${right} ${bot - r}`,
      `A ${r} ${r} 0 0 1 ${right - r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    return { traceRight: traceUp, traceLeft: traceDown };
  }
  if (onRight) {
    const traceDown = [
      `M ${right} ${sy}`,
      `L ${right} ${bot - r}`,
      `A ${r} ${r} 0 0 1 ${right - r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    const traceUp = [
      `M ${right} ${sy}`,
      `L ${right} ${top + r}`,
      `A ${r} ${r} 0 0 1 ${right - r} ${top}`,
      `L ${left + r} ${top}`,
      `A ${r} ${r} 0 0 0 ${left} ${top + r}`,
      `L ${left} ${bot - r}`,
      `A ${r} ${r} 0 0 0 ${left + r} ${bot}`,
      `L ${ex} ${bot}`,
    ].join(" ");
    return { traceRight: traceDown, traceLeft: traceUp };
  }
  return borderTracePaths(w, h, r, w * 0.5, top);
}

/** Read the painted top-left corner radius for border-trace alignment. */
export function passiveNodeBorderRadius(node) {
  if (!node) return PICK_BORDER_R;
  const br = parseFloat(getComputedStyle(node).borderTopLeftRadius);
  return Number.isFinite(br) && br > 0 ? br : PICK_BORDER_R;
}

/** Animate golden border trace on a pick node from anchor (local coords). */
export function startBorderReveal(node, ax, ay, opts = {}) {
  const borderMs = opts.borderMs ?? PICK_UNLOCK_BORDER_MS;
  const borderR = opts.borderR ?? passiveNodeBorderRadius(node);
  node.classList.add("border-revealing");
  node.style.setProperty("--passive-unlock-border-ms", `${borderMs}ms`);
  node.style.setProperty("--passive-reveal-r", `${borderR}px`);
  node.querySelector(".passive-border-trace")?.remove();
  const w = node.offsetWidth;
  const h = node.offsetHeight;
  if (w < 4 || h < 4) return;

  const { traceRight, traceLeft } = borderTracePaths(w, h, borderR, ax, ay);
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", "passive-border-trace");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);

  const mkPath = (d) => {
    const p = document.createElementNS(ns, "path");
    p.setAttribute("d", d);
    svg.appendChild(p);
    return p;
  };
  const pR = mkPath(traceRight);
  const pL = mkPath(traceLeft);
  node.appendChild(svg);

  const animPath = (p) => {
    const dashLen = p.getTotalLength();
    p.style.strokeDasharray = `${dashLen}`;
    p.style.strokeDashoffset = `${dashLen}`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        p.style.transition = `stroke-dashoffset ${borderMs}ms ease-out`;
        p.style.strokeDashoffset = "0";
      });
    });
  };
  animPath(pR);
  animPath(pL);
}

/** Draw a glowing connection line between viewport coords, then call onEdgeDone. */
export function animateConnectionLine(svg, x1, y1, x2, y2, onEdgeDone, opts = {}) {
  const startMs = opts.startMs ?? PICK_UNLOCK_START_MS;
  const edgeMs = opts.edgeMs ?? PICK_UNLOCK_EDGE_MS;
  if (!svg) {
    onEdgeDone?.();
    return;
  }
  svg.innerHTML = "";
  svg.setAttribute("viewBox", `0 0 ${window.innerWidth} ${window.innerHeight}`);

  const ns = "http://www.w3.org/2000/svg";
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", `M ${x1} ${y1} L ${x2} ${y2}`);
  path.setAttribute("class", "pick-unlock-edge");
  svg.appendChild(path);

  const mkDot = (x, y) => {
    const c = document.createElementNS(ns, "circle");
    c.setAttribute("cx", String(x));
    c.setAttribute("cy", String(y));
    c.setAttribute("r", "4");
    c.setAttribute("class", "pick-unlock-joint");
    svg.appendChild(c);
    return c;
  };
  const fromDot = mkDot(x1, y1);
  fromDot.classList.add("joint-anim-start");
  const toDot = mkDot(x2, y2);

  const dashLen = path.getTotalLength();
  path.style.strokeDasharray = `${dashLen}`;
  path.style.strokeDashoffset = `${dashLen}`;

  setTimeout(() => {
    fromDot.classList.remove("joint-anim-start");
    fromDot.classList.add("joint-lit");
    requestAnimationFrame(() => {
      path.style.transition = `stroke-dashoffset ${edgeMs}ms ease-out`;
      path.style.strokeDashoffset = "0";
    });
  }, startMs);

  setTimeout(() => {
    toDot.classList.add("joint-lit", "joint-anim-pop");
    path.style.transition = "";
    onEdgeDone?.();
  }, startMs + edgeMs);
}

/** Full sequence: line from source → border trace → golden owned-reveal on node. */
export function runPickUnlockAnim(node, lineSvg, fromX, fromY, toX, toY, onComplete, opts = {}) {
  const startMs = opts.startMs ?? PICK_UNLOCK_START_MS;
  const edgeMs = opts.edgeMs ?? PICK_UNLOCK_EDGE_MS;
  const dotMs = opts.dotMs ?? PICK_UNLOCK_DOT_MS;
  const borderMs = opts.borderMs ?? PICK_UNLOCK_BORDER_MS;
  const lineOpts = { startMs, edgeMs };

  const finish = () => {
    node.classList.remove("just-unlocked", "border-revealing", "draft-committing");
    node.classList.add("owned-reveal");
    node.querySelector(".passive-border-trace")?.remove();
    if (lineSvg) lineSvg.innerHTML = "";
    onComplete?.();
  };

  if (!node) {
    onComplete?.();
    return;
  }

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    node.classList.add("owned-reveal");
    onComplete?.();
    return;
  }

  node.classList.add("just-unlocked", "draft-committing");

  const startBorder = () => {
    startBorderReveal(node, node.offsetWidth * 0.5, 0, { borderMs });
    setTimeout(finish, borderMs);
  };

  animateConnectionLine(lineSvg, fromX, fromY, toX, toY, () => {
    setTimeout(startBorder, dotMs);
  }, lineOpts);
}
